/** Indicadores de servicios básicos — Censo 2022 / Poblaciones (radios). */
export const SERVICE_DEFICIT_INDICATORS = [
  {
    key: 'water_network_deficit_pct',
    lotKey: 'lot_water_network_deficit_pct',
    shortLabel: 'Agua de red',
    listAbbrev: 'agua',
    serviceName: 'agua de red',
  },
  {
    key: 'sewer_deficit_pct',
    lotKey: 'lot_sewer_deficit_pct',
    shortLabel: 'Cloaca',
    listAbbrev: 'cloaca',
    serviceName: 'conexión cloacal',
  },
  {
    key: 'cooking_fuel_deficit_pct',
    lotKey: 'lot_cooking_fuel_deficit_pct',
    shortLabel: 'Gas de red',
    listAbbrev: 'gas',
    serviceName: 'gas de red',
  },
];

export const SERVICE_DEFICIT_BUCKETS = [
  { bucket: 0, label: 'Sin dato', color: '#9E9E9E' },
  { bucket: 1, label: 'Menor que 20%', color: '#4CAF50' },
  { bucket: 2, label: '20 a 30%', color: '#FFC107' },
  { bucket: 3, label: '30 a 40%', color: '#FF9800' },
  { bucket: 4, label: '40% y más', color: '#F44336' },
];

const MISSING_VALUE_COLOR = 'rgba(255,255,255,0.38)';
const HAS_SERVICE_COLOR = '#4CAF50';
const LACKS_SERVICE_COLOR = '#F44336';

/** % de hogares sin servicio a partir del cual el radio se considera sin acceso generalizado. */
export const SERVICE_DEFICIT_SIGNIFICANT_THRESHOLD = 40;

/** Parsea % de déficit de servicios (0–100). null = ausente en API. */
export const parseServiceDeficitPct = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n) || Number.isNaN(n)) return null;
  return Math.min(100, Math.max(0, n));
};

/** Bucket 0–4 según bandas Poblaciones (radios 2022). */
export const serviceDeficitBucket = (pct) => {
  if (pct == null || Number.isNaN(pct)) return 0;
  const p = Number(pct);
  if (p < 20) return 1;
  if (p < 30) return 2;
  if (p < 40) return 3;
  return 4;
};

export const getServiceDeficitBucketMeta = (bucket) => (
  SERVICE_DEFICIT_BUCKETS.find((item) => item.bucket === bucket) ?? SERVICE_DEFICIT_BUCKETS[0]
);

export const formatServiceDeficitPct = (pct) => {
  const parsed = parseServiceDeficitPct(pct);
  if (parsed === null) return null;
  return `${new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(parsed)}%`;
};

export const getServiceDeficitValueFromLot = (lot, indicator) => {
  if (!lot || !indicator) return null;
  return parseServiceDeficitPct(lot[indicator.key] ?? lot[indicator.lotKey] ?? null);
};

/** true si el déficit en el radio supera el umbral (sin acceso generalizado). */
export const isSignificantServiceDeficit = (pct) => {
  const parsed = parseServiceDeficitPct(pct);
  if (parsed === null) return null;
  return parsed >= SERVICE_DEFICIT_SIGNIFICANT_THRESHOLD;
};

export const buildServiceDeficitTooltipMessage = ({
  serviceName = 'este servicio',
  pctLabel = null,
  isMissing = false,
  hasAccess = false,
}) => {
  if (isMissing) {
    return `No hay datos de ${serviceName} para el radio censal de este lote.`;
  }

  if (hasAccess) {
    return (
      `El radio censal tiene acceso generalizado a ${serviceName}. `
      + `Solo el ${pctLabel} de los hogares no cuenta con el servicio.`
    );
  }

  return (
    `El radio censal no tiene acceso generalizado a ${serviceName}. `
    + `El ${pctLabel} de los hogares no cuenta con el servicio.`
  );
};

/**
 * Presentación de acceso a servicio en el radio censal (Sí / No / Sin dato).
 * Sí = acceso generalizado (déficit &lt; 40%). El % solo va en el tooltip.
 */
export const getServiceDeficitPanelDisplay = (value, indicator) => {
  const parsed = parseServiceDeficitPct(value);
  const isMissing = parsed === null;
  const bucket = serviceDeficitBucket(isMissing ? null : parsed);
  const bucketMeta = getServiceDeficitBucketMeta(bucket);
  const pctLabel = isMissing ? null : formatServiceDeficitPct(parsed);
  const lacksGeneralizedAccess = isMissing ? null : isSignificantServiceDeficit(parsed);
  const hasAccess = isMissing ? null : !lacksGeneralizedAccess;
  const valueLabel = isMissing ? 'Sin dato' : (hasAccess ? 'Sí' : 'No');

  const accentColor = isMissing
    ? MISSING_VALUE_COLOR
    : (hasAccess ? HAS_SERVICE_COLOR : LACKS_SERVICE_COLOR);

  return {
    status: 'ready',
    isMissing,
    pct: parsed,
    pctLabel,
    valueLabel,
    hasAccess,
    rangeLabel: isMissing ? null : bucketMeta.label,
    bucket,
    isSignificant: lacksGeneralizedAccess,
    isCritical: lacksGeneralizedAccess === true,
    accentColor,
    tooltip: buildServiceDeficitTooltipMessage({
      serviceName: indicator?.serviceName ?? 'este servicio',
      pctLabel,
      isMissing,
      hasAccess: hasAccess === true,
    }),
  };
};

export const pickServiceDeficitFields = (source, fallback = {}) => ({
  water_network_deficit_pct:
    source?.water_network_deficit_pct
    ?? source?.lot_water_network_deficit_pct
    ?? fallback.water_network_deficit_pct
    ?? null,
  sewer_deficit_pct:
    source?.sewer_deficit_pct
    ?? source?.lot_sewer_deficit_pct
    ?? fallback.sewer_deficit_pct
    ?? null,
  cooking_fuel_deficit_pct:
    source?.cooking_fuel_deficit_pct
    ?? source?.lot_cooking_fuel_deficit_pct
    ?? fallback.cooking_fuel_deficit_pct
    ?? null,
});
